// Session Status Component for MUU Platform
import React, { memo } from 'react';
import {
  Chip,
  Tooltip,
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AccessTime,
  Person,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { useFormattedSessionTime } from '../../hooks/useSessionTime';
import { DESIGN_TOKENS } from '../../design-system/tokens/colors';

interface SessionStatusProps {
  variant?: 'chip' | 'detailed' | 'minimal';
  showUser?: boolean;
  size?: 'small' | 'medium';
}

/**
 * Componente para mostrar o status da sessão do usuário
 */
export const SessionStatus: React.FC<SessionStatusProps> = memo(({
  variant = 'chip',
  showUser = false,
  size = 'small'
}) => {
  const { authState } = useAuth();
  const { shortFormat, humanReadable, hours, minutes } = useFormattedSessionTime();
  const theme = useTheme();

  if (!authState.isAuthenticated || !authState.user) {
    return null;
  }

  const getSessionColor = () => {
    if (hours >= 8) return 'warning'; // Mais de 8 horas
    if (hours >= 4) return 'info'; // Mais de 4 horas
    return 'success'; // Menos de 4 horas
  };

  const getTooltipText = () => {
    const startTime = authState.sessionStartTime 
      ? new Date(authState.sessionStartTime).toLocaleTimeString('pt-BR')
      : 'Desconhecido';
    
    return `Logado há ${humanReadable}\nInício da sessão: ${startTime}`;
  };

  if (variant === 'minimal') {
    return (
      <Tooltip title={getTooltipText()} arrow>
        <Chip
          icon={<AccessTime sx={{ fontSize: 16 }} />}
          label={shortFormat}
          size={size}
          color={getSessionColor()}
          variant="outlined"
          sx={{
            fontSize: '0.75rem',
            height: 24,
          }}
        />
      </Tooltip>
    );
  }

  if (variant === 'detailed') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {showUser && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person sx={{ color: DESIGN_TOKENS.colors.primary[500] }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {authState.user.full_name || authState.user.email}
            </Typography>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTime sx={{ color: getSessionColor() === 'success' ? 'green' : getSessionColor() === 'warning' ? 'orange' : 'blue' }} />
          <Typography variant="body2">
            Logado há {shortFormat}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Default: chip variant
  return (
    <Tooltip title={getTooltipText()} arrow>
      <Chip
        icon={<AccessTime sx={{ fontSize: 16 }} />}
        label={`Logado há ${shortFormat}`}
        size={size}
        color={getSessionColor()}
        sx={{
          fontSize: '0.8rem',
          '& .MuiChip-icon': {
            marginLeft: 1,
          },
        }}
      />
    </Tooltip>
  );
});

export default SessionStatus;